<script setup>
import {useMatchedBetStore} from "@/stores/MatchedBetStore";
import {computed} from "vue";

const matchedBetStore = useMatchedBetStore();

const props = defineProps({
  winBackBet: Object,
  winLayBet: Object,
  placeBackBet: Object,
  placeLayBet: Object,
  showExtraPlace: {
    type: Boolean,
    default: false,
  },
});

const resultsWin = computed(() => matchedBetStore.getResults(props.winBackBet, props.winLayBet));
const resultsPlace = computed(() => matchedBetStore.getResults(props.placeBackBet, props.placeLayBet));

const formatValue = (value) => {
  const isPositive = value >= 0;
  return {
    text: `${isPositive ? '+' : '-'}£${Math.abs(value).toFixed(2)}`,
    class: isPositive ? 'text-green' : 'text-red',
  };
};

// New computed properties for implied odds calculation
const totalFirstPlace = computed(() => resultsWin.value.totalBackWin + resultsPlace.value.totalBackWin);
const totalStandardPlace = computed(() => resultsWin.value.totalLayWin + resultsPlace.value.totalBackWin);
const totalDoesntPlace = computed(() => resultsWin.value.totalLayWin + resultsPlace.value.totalLayWin);
const totalExtraPlace = computed(() => resultsWin.value.totalLayWin + resultsPlace.value.layWin + resultsPlace.value.backWin);

const qualifyingLoss = computed(() => {
  const totals = [totalFirstPlace.value, totalStandardPlace.value, totalDoesntPlace.value];
  // Filter the totals to find the losses (negative totals)
  const losses = totals.filter((total) => total < 0);
  if (losses.length === 0) {
    return 0;
  } else {
    // Return the maximum loss (closest to zero)
    return Math.max(...losses);
  }
});

const impliedOdds = computed(() => {
  if (qualifyingLoss.value >= 0) {
    return null;
  }
  return (totalExtraPlace.value + Math.abs(qualifyingLoss.value)) / Math.abs(qualifyingLoss.value);
});
</script>

<template>
  <div class="matched-bet-results-container">
    <table class="bet-table large-screen-table">
      <thead>
      <tr>
        <th></th>
        <th>Bookie</th>
        <th>Exchange</th>
        <th>Total</th>
      </tr>
      </thead>
      <tbody>
      <tr>
        <td>First place</td>
        <td>
          <div class="cell-content">
            <span :class="formatValue(resultsWin.backWin).class">{{ formatValue(resultsWin.backWin).text }}</span>
            <span :class="formatValue(resultsPlace.backWin).class">{{ formatValue(resultsPlace.backWin).text }}</span>
          </div>
        </td>
        <td>
          <div class="cell-content">
            <span :class="formatValue(resultsWin.layLoss).class">{{ formatValue(resultsWin.layLoss).text }}</span>
            <span :class="formatValue(resultsPlace.layLoss).class">{{ formatValue(resultsPlace.layLoss).text }}</span>
          </div>
        </td>
        <td :class="formatValue(totalFirstPlace).class">
          {{ formatValue(totalFirstPlace).text }}
        </td>
      </tr>

      <tr>
        <td>Standard place</td>
        <td>
          <div class="cell-content">
            <span :class="formatValue(resultsWin.backLoss).class">{{ formatValue(resultsWin.backLoss).text }}</span>
            <span :class="formatValue(resultsPlace.backWin).class">{{ formatValue(resultsPlace.backWin).text }}</span>
          </div>
        </td>
        <td>
          <div class="cell-content">
            <span :class="formatValue(resultsWin.layWin).class">{{ formatValue(resultsWin.layWin).text }}</span>
            <span :class="formatValue(resultsPlace.layLoss).class">{{ formatValue(resultsPlace.layLoss).text }}</span>
          </div>
        </td>
        <td :class="formatValue(totalStandardPlace).class">
          {{ formatValue(totalStandardPlace).text }}
        </td>
      </tr>


      <tr v-if="showExtraPlace">
        <td>Extra place</td>
        <td>
          <div class="cell-content">
            <span :class="formatValue(resultsWin.backLoss).class">{{ formatValue(resultsWin.backLoss).text }}</span>
            <span :class="formatValue(resultsPlace.backWin).class">{{ formatValue(resultsPlace.backWin).text }}</span>
          </div>
        </td>
        <td>
          <div class="cell-content">
            <span :class="formatValue(resultsWin.layWin).class">{{ formatValue(resultsWin.layWin).text }}</span>
            <span :class="formatValue(resultsPlace.layWin).class">{{ formatValue(resultsPlace.layWin).text }}</span>
          </div>
        </td>
        <td :class="formatValue(totalExtraPlace).class">
          {{ formatValue(totalExtraPlace).text }}
        </td>
      </tr>


      <tr>
        <td>Doesn't place</td>
        <td>
          <div class="cell-content">
            <span :class="formatValue(resultsWin.backLoss).class">{{ formatValue(resultsWin.backLoss).text }}</span>
            <span :class="formatValue(resultsPlace.backLoss).class">{{ formatValue(resultsPlace.backLoss).text }}</span>
          </div>
        </td>
        <td>
          <div class="cell-content">
            <span :class="formatValue(resultsWin.layWin).class">{{ formatValue(resultsWin.layWin).text }}</span>
            <span :class="formatValue(resultsPlace.layWin).class">{{ formatValue(resultsPlace.layWin).text }}</span>
          </div>
        </td>
        <td :class="formatValue(totalDoesntPlace).class">
          {{ formatValue(totalDoesntPlace).text }}
        </td>
      </tr>
      <tr v-if="showExtraPlace">
        <td colspan="4" class="implied-odds-row">
          <span style="display: flex"><span style="margin-inline: auto">Implied Odds: {{
              impliedOdds ? impliedOdds.toFixed(2) : 'N/A'
            }}</span></span>
        </td>
      </tr>
      </tbody>
    </table>
  </div>
</template>

<style scoped>

.matched-bet-results-container {
  margin-top: 0.5rem;
  background-color: var(--clr-black-10);
}

.text-green {
  color: green;
}

.text-red {
  color: red;
}

.bet-table {
  width: 100%;
  margin-bottom: 1rem;
  border-collapse: collapse; /* Ensure no extra space between table cells */
}

.bet-table th, .bet-table td {
  padding: 1rem; /* Increase padding for better readability */
  text-align: center; /* Center-align text */
  border: 1px solid #ddd;
  vertical-align: middle; /* Ensure text is vertically centered */
}

.bet-table th:first-child,
.bet-table td:first-child {
  border-left: none;
}

.bet-table th {
  border-top: none;
}

.bet-table th:last-child,
.bet-table td:last-child {
  border-right: none;
}

.bet-table tbody tr:last-child td {
  border-bottom: none;
}

.bet-table th {
  font-weight: var(--fw-semi-bold);
  background-color: #f4f4f4; /* Light background color for headers */
}

.bet-table td:first-child {
  font-weight: var(--fw-semi-bold);
  text-align: left; /* Align the first column's text to the left */
}

.large-screen-table {
  display: table;
}

.bet-table .cell-content {
  display: flex;
  justify-content: center; /* Center the spans inside the td */
  gap: 0.5rem; /* 1rem space between spans */
  align-items: center;
}


.implied-odds-row {
  text-align: center; /* Ensure content is centered */
  font-weight: bold;
  background-color: var(--clr-primary-100); /* Primary color */
  color: white; /* White text */
  padding: 1.5rem 0; /* Add padding to make the row stand out */
  font-size: 1.2rem; /* Increase font size for emphasis */
  width: 100%;
}





</style>

